import React, { useState, useEffect } from 'react';
import {
  ClipboardDocumentIcon,
  CheckIcon,
  PhotoIcon,
  HashtagIcon,
  InformationCircleIcon,
  ChatBubbleLeftRightIcon,
  GlobeAltIcon,
  PaperAirplaneIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi } from '../../api/marketplace';
import { useAuth } from '../../contexts/AuthContext';
import { getFacilityId } from '../../utils/facilityUtils';
import toast from 'react-hot-toast';
import {
  BrandModal,
  ModalCloseButton,
} from '../ui/BrandModal';

interface FormattedListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  listingId: string;
  listingTitle: string;
}

interface PlatformInfo {
  name: string;
  icon: string;
  color: string;
  description: string;
}

const PLATFORMS: Record<string, PlatformInfo> = {
  whatsapp: {
    name: 'WhatsApp',
    icon: '💬',
    color: 'bg-green-500',
    description: 'Perfect for direct messaging and group chats',
  },
  facebook: {
    name: 'Facebook',
    icon: '📘',
    color: 'bg-cyan-600',
    description: 'Great for marketplace posts and community groups',
  },
  telegram: {
    name: 'Telegram',
    icon: '📡',
    color: 'bg-cyan-500',
    description: 'Ideal for channels and large group communications',
  },
  instagram: {
    name: 'Instagram',
    icon: '📷',
    color: 'bg-pink-600',
    description: 'Perfect for visual content and stories',
  },
};

export default function FormattedListingModal({
  isOpen,
  onClose,
  listingId,
  listingTitle,
}: FormattedListingModalProps) {
  const { user } = useAuth();
  const [selectedPlatform, setSelectedPlatform] = useState<string>('whatsapp');
  const [formattedText, setFormattedText] = useState<string>('');
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [copyInstructions, setCopyInstructions] = useState<string>('');
  const [templateMedia, setTemplateMedia] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string>('');
  const [showHashtags, setShowHashtags] = useState(false);
  const [showMedia, setShowMedia] = useState(false);

  // Use the utility function to get a proper facility ID
  const facilityId = getFacilityId(user?.facilityId);

  useEffect(() => {
    if (isOpen && listingId) {
      loadFormattedListing();
    }
  }, [isOpen, listingId, selectedPlatform]);

  const loadFormattedListing = async () => {
    try {
      setLoading(true);
      
      // Clear any cached data by adding a timestamp parameter
      const timestamp = Date.now();
      const data = await marketplaceApi.getFormattedListing(
        facilityId, 
        listingId, 
        selectedPlatform as 'whatsapp' | 'facebook' | 'telegram' | 'instagram',
        timestamp // Add timestamp to force fresh data
      );
      
              setFormattedText(data.formattedText);
        setHashtags(data.hashtags);
        setMediaUrls(data.mediaUrls);
        setCopyInstructions(data.copyInstructions);
        setTemplateMedia(data.templateMedia || null);
    } catch (error) {
      console.error('Error loading formatted listing:', error);
      toast.error('Failed to load formatted listing');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(''), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const copyTextWithHashtags = () => {
    if (!formattedText) return;
    const textWithHashtags = formattedText + '\n\n' + hashtags.join(' ');
    copyToClipboard(textWithHashtags);
  };

  const copyTextOnly = () => {
    if (!formattedText) return;
    copyToClipboard(formattedText);
  };

  const copyHashtagsOnly = () => {
    if (!hashtags.length) return;
    copyToClipboard(hashtags.join(' '));
  };

  // Format text with rich text formatting for display
  const formatTextForDisplay = (text: string, platform: string) => {
    let formatted = text;

    // First, handle line breaks by converting them to <br> tags
    formatted = formatted.replace(/\n/g, '<br>');

    switch (platform) {
      case 'whatsapp':
        // WhatsApp formatting
        formatted = formatted
          .replace(/\*(.*?)\*/g, '<strong>$1</strong>') // Bold
          .replace(/_(.*?)_/g, '<em>$1</em>') // Italic
          .replace(/~(.*?)~/g, '<del>$1</del>') // Strikethrough
          .replace(/`(.*?)`/g, '<code>$1</code>'); // Monospace
        break;
      
      case 'telegram':
        // Telegram formatting
        formatted = formatted
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
          .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
          .replace(/__(.*?)__/g, '<u>$1</u>') // Underline
          .replace(/~~(.*?)~~/g, '<del>$1</del>') // Strikethrough
          .replace(/`(.*?)`/g, '<code>$1</code>'); // Monospace
        break;
      
      case 'instagram':
      case 'facebook':
        // Instagram and Facebook don't support text formatting, but show basic formatting for preview
        formatted = formatted
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
          .replace(/\*(.*?)\*/g, '<em>$1</em>'); // Italic
        break;
    }

    return formatted;
  };

  if (!isOpen) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <div className="modal-header flex items-start justify-between gap-4 bg-cyan-600 !border-cyan-600">
        <div className="flex items-center min-w-0 text-left">
          <DevicePhoneMobileIcon className="h-6 w-6 text-white mr-2 shrink-0" />
          <h3 className="text-lg font-medium text-white">
            Formatted Listing - {listingTitle}
          </h3>
        </div>
        <ModalCloseButton onClose={onClose} className="text-white hover:bg-cyan-500" />
      </div>

          {/* Platform Selection */}
          <div className="modal-body !py-3 border-b border-gray-200">
            <div className="flex space-x-2">
              {Object.entries(PLATFORMS).map(([id, platform]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelectedPlatform(id)}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedPlatform === id
                      ? `${platform.color} text-white`
                      : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-2">{platform.icon}</span>
                  {platform.name}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="modal-body">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                <span className="ml-2 text-slate-600">Generating formatted listing...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Instructions */}
                <div className="bg-cyan-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <InformationCircleIcon className="h-5 w-5 text-cyan-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-sm text-cyan-800">
                      <p className="font-medium">Copy Instructions:</p>
                      <p>{copyInstructions}</p>
                    </div>
                  </div>
                </div>

                {/* Formatted Text */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-slate-700">Formatted Text</h4>
                    <div className="flex space-x-2">
                      <button
                        onClick={copyTextOnly}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-cyan-600 bg-cyan-50 rounded hover:bg-cyan-100"
                      >
                        {copied === formattedText ? (
                          <>
                            <CheckIcon className="h-3 w-3 mr-1" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                            Copy Text
                          </>
                        )}
                      </button>
                      <button
                        onClick={copyTextWithHashtags}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100"
                      >
                        <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                        Copy with Hashtags
                      </button>
                    </div>
                  </div>
                  <div className="bg-slate-50/80 border border-gray-200 rounded-lg p-4">
                    <div 
                      className="text-sm text-gray-800 font-mono"
                      dangerouslySetInnerHTML={{ 
                        __html: formatTextForDisplay(formattedText, selectedPlatform) 
                      }}
                    />
                  </div>
                </div>

                {/* Hashtags */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-slate-700">Hashtags</h4>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowHashtags(!showHashtags)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-slate-600 bg-slate-50/80 rounded hover:bg-gray-100"
                      >
                        <HashtagIcon className="h-3 w-3 mr-1" />
                        {showHashtags ? 'Hide' : 'Show'}
                      </button>
                      <button
                        onClick={copyHashtagsOnly}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100"
                      >
                        <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                        Copy
                      </button>
                    </div>
                  </div>
                  {showHashtags && (
                    <div className="bg-slate-50/80 border border-gray-200 rounded-lg p-3">
                      <div className="flex flex-wrap gap-2">
                        {hashtags.map((hashtag, index) => (
                          <span
                            key={index}
                            className="inline-block px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded"
                          >
                            {hashtag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                                 {/* Template Media Information */}
                 {templateMedia && (
                   <div>
                     <div className="flex items-center justify-between mb-2">
                       <h4 className="text-sm font-medium text-slate-700">Template Media Settings</h4>
                     </div>
                     <div className="bg-cyan-50 border border-blue-200 rounded-lg p-3">
                       <div className="grid grid-cols-2 gap-2 text-xs text-cyan-800">
                         <div>📷 Images: {templateMedia.includeImages ? 'Yes' : 'No'}</div>
                         {templateMedia.includeImages && (
                           <div>Count: {templateMedia.imageCount}</div>
                         )}
                         <div>🎥 Video: {templateMedia.includeVideo ? 'Yes' : 'No'}</div>
                         <div>📄 Documents: {templateMedia.includeDocuments ? 'Yes' : 'No'}</div>
                       </div>
                     </div>
                   </div>
                 )}

                 {/* Media URLs */}
                 {mediaUrls.length > 0 && (
                   <div>
                     <div className="flex items-center justify-between mb-2">
                       <h4 className="text-sm font-medium text-slate-700">Media Files ({mediaUrls.length})</h4>
                       <button
                         onClick={() => setShowMedia(!showMedia)}
                         className="inline-flex items-center px-2 py-1 text-xs font-medium text-slate-600 bg-slate-50/80 rounded hover:bg-gray-100"
                       >
                         <PhotoIcon className="h-3 w-3 mr-1" />
                         {showMedia ? 'Hide' : 'Show'} URLs
                       </button>
                     </div>
                     {showMedia && (
                       <div className="bg-slate-50/80 border border-gray-200 rounded-lg p-3">
                         <div className="space-y-2">
                           {mediaUrls.map((url, index) => (
                             <div key={index} className="flex items-center justify-between">
                               <span className="text-xs text-slate-600 truncate flex-1 mr-2">
                                 {url}
                               </span>
                               <button
                                 onClick={() => copyToClipboard(url)}
                                 className="inline-flex items-center px-2 py-1 text-xs font-medium text-cyan-600 bg-cyan-50 rounded hover:bg-blue-100"
                               >
                                 <ClipboardDocumentIcon className="h-3 w-3" />
                               </button>
                             </div>
                           ))}
                         </div>
                       </div>
                     )}
                   </div>
                 )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-primary text-sm"
            >
              Close
            </button>
          </div>
    </BrandModal>
  );
}
